/**
 * Collection Filters AJAX Handler
 *
 * Handles AJAX-based filtering, sorting, and pagination for collection pages
 * Uses Shopify Section Rendering API to fetch updated HTML without page refresh
 *
 * Features:
 * - Desktop sidebar filter AJAX
 * - Mobile drawer filter AJAX
 * - Reset buttons AJAX
 * - Sort dropdown AJAX
 * - Pagination AJAX
 * - URL state management (history.pushState)
 * - Loading states
 * - Error handling
 * - Request debouncing (300ms)
 * - Request cancellation (AbortController)
 *
 * @version 1.0.0
 */

class CollectionFiltersAjax {
  constructor() {
    // Get section ID from DOM
    const sectionElement = document.querySelector('[data-section-id]');
    if (!sectionElement) {
      console.warn('CollectionFiltersAjax: No [data-section-id] element found');
      return;
    }

    this.sectionId = sectionElement.getAttribute('data-section-id');

    // DOM elements
    this.productGrid = document.getElementById('ProductGrid');
    this.productCount = document.getElementById('ProductCount');
    this.pagination = document.getElementById('Pagination');
    this.desktopFilters = document.getElementById('DesktopFilters');
    this.mobileDrawer = document.getElementById('MobileDrawerFilters');
    this.mobileDrawerTriggerContainer = document.getElementById('MobileDrawerTrigger');
    this.loadingOverlay = document.querySelector('[data-loading-overlay]');
    this.mobileDrawerTrigger = document.querySelector('[data-drawer-trigger]');

    // Forms
    this.desktopForm = document.getElementById('filter-form');
    this.mobileForm = document.getElementById('filter-form-mobile');

    // State
    this.abortController = null;

    // Cache for AJAX responses (Step 12)
    this.cache = new Map();
    this.MAX_CACHE_SIZE = 10;

    // Initialize
    this.init();
  }

  init() {
    this.setupHistoryManagement();
    this.bindDesktopFilterEvents();
    this.bindMobileDrawerEvents();
    this.bindDesktopResetFilterButtons();
    this.bindMobileResetFilterButtons();
    this.bindActiveFilterPills();
    this.bindSortEvents();
    this.bindPaginationEvents();
  }

  /**
   * Bind desktop filter events (checkboxes and price radios)
   * Uses 300ms debounce to avoid excessive requests
   */
  bindDesktopFilterEvents() {
    if (!this.desktopForm) return;

    // Create debounced handler for filter changes
    const debouncedHandler = this.debounce(() => {
      this.handleDesktopFilterChange();
    }, 300);

    // Bind checkbox events
    const checkboxes = this.desktopForm.querySelectorAll('[data-filter-checkbox]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', debouncedHandler);
    });

    // Bind price radio events
    const priceRadios = this.desktopForm.querySelectorAll('[data-price-radio]');
    priceRadios.forEach(radio => {
      radio.addEventListener('change', debouncedHandler);
    });
  }

  /**
   * Handle desktop filter change - serialize form and trigger AJAX
   */
  handleDesktopFilterChange() {
    if (!this.desktopForm) return;

    // Serialize form data to URL search params
    const formData = new FormData(this.desktopForm);
    const searchParams = new URLSearchParams(formData).toString();

    // Trigger AJAX page render
    this.renderPage(searchParams);
  }

  /**
   * Bind mobile drawer "View Results" button
   */
  bindMobileDrawerEvents() {
    // Bind "View Results" button click
    const applyBtn = document.querySelector('[data-filter-drawer-apply]');
    if (applyBtn) {
      applyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleMobileDrawerApply();
      });
    }
  }

  /**
   * Handle "View Results" button click - close drawer and update grid
   */
  handleMobileDrawerApply() {
    if (!this.mobileForm) return;

    // Serialize form data to URL search params
    const formData = new FormData(this.mobileForm);
    const searchParams = new URLSearchParams(formData).toString();

    // Close the drawer (using the existing CollectionFilters class method)
    if (window.collectionFilters && window.collectionFilters.closeDrawer) {
      window.collectionFilters.closeDrawer();
    }

    // Trigger AJAX page render
    this.renderPage(searchParams);
  }

  /**
   * Bind sort dropdown change event
   */
  bindSortEvents() {
    const sortSelect = document.querySelector('[data-sort-select]');
    if (!sortSelect) return;

    // Remove existing listener if any (prevent duplicates)
    if (this.sortChangeHandler) {
      sortSelect.removeEventListener('change', this.sortChangeHandler);
    }

    // Store handler reference for cleanup
    this.sortChangeHandler = (e) => {
      this.handleSortChange(e.target.value);
    };

    sortSelect.addEventListener('change', this.sortChangeHandler);
  }

  /**
   * Bind pagination link click events
   */
  bindPaginationEvents() {
    const paginationLinks = document.querySelectorAll('[data-pagination-link]');

    paginationLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePaginationClick(link.href);
      });
    });
  }

  /**
   * Handle pagination link click
   * @param {string} href - The full href of the pagination link
   */
  handlePaginationClick(href) {
    // Extract search params from href
    const url = new URL(href, window.location.origin);
    const searchParams = url.search.substring(1); // Remove leading '?'

    // Trigger AJAX page render
    this.renderPage(searchParams);
  }

  /**
   * Handle sort dropdown change
   * @param {string} sortValue - The selected sort value
   */
  handleSortChange(sortValue) {
    // Get current URL search params
    const currentParams = new URLSearchParams(window.location.search);

    // Update or remove sort_by parameter
    if (sortValue === 'manual' || sortValue === '') {
      currentParams.delete('sort_by');
    } else {
      currentParams.set('sort_by', sortValue);
    }

    // Remove page parameter (reset to page 1 when sorting)
    currentParams.delete('page');

    // Trigger AJAX page render
    this.renderPage(currentParams.toString());
  }

  /**
   * Bind desktop Reset and Reset All filter button events
   */
  bindDesktopResetFilterButtons() {
    // Desktop Reset All Filters button
    const desktopResetAllFilters = document.querySelector('[data-clear-all]');
    if (desktopResetAllFilters) {
      desktopResetAllFilters.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(e.currentTarget.href, window.location.origin);
        const searchParams = url.search.substring(1); // Remove leading '?'
        this.renderPage(searchParams);
      });
    }

    // Desktop individual Reset Filter buttons (per filter group) - scoped to desktop filters
    const desktopResetFilterBtns = document.querySelectorAll('#DesktopFilters [data-clear-filter]');
    desktopResetFilterBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(e.currentTarget.href, window.location.origin);
        const searchParams = url.search.substring(1);
        this.renderPage(searchParams);
      });
    });
  }

  /**
   * Bind mobile Reset and Reset All filter button events
   */
  bindMobileResetFilterButtons() {
    // Mobile Reset All Filters button (close drawer first)
    const mobileResetAllFilters = document.querySelector('[data-clear-all-mobile]');
    if (mobileResetAllFilters) {
      mobileResetAllFilters.addEventListener('click', (e) => {
        e.preventDefault();

        // Close the drawer
        if (window.collectionFilters && window.collectionFilters.closeDrawer) {
          window.collectionFilters.closeDrawer();
        }

        const url = new URL(e.currentTarget.href, window.location.origin);
        const searchParams = url.search.substring(1);
        this.renderPage(searchParams);
      });
    }

    // Mobile individual Reset Filter buttons (close drawer first) - scoped to mobile drawer
    const mobileResetFilterBtns = document.querySelectorAll('#MobileDrawerFilters [data-clear-filter-mobile]');
    mobileResetFilterBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        // Close the drawer
        if (window.collectionFilters && window.collectionFilters.closeDrawer) {
          window.collectionFilters.closeDrawer();
        }

        const url = new URL(e.currentTarget.href, window.location.origin);
        const searchParams = url.search.substring(1);
        this.renderPage(searchParams);
      });
    });
  }

  /**
   * Bind active filter pill removal events (desktop sidebar only)
   * Mobile drawer doesn't display active filter pills - it uses Reset Filter buttons instead
   */
  bindActiveFilterPills() {
    // Only bind pills in desktop sidebar
    const desktopPills = document.querySelectorAll('#active-filters-desktop [data-active-filter-remove]');

    desktopPills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(e.currentTarget.href, window.location.origin);
        const searchParams = url.search.substring(1); // Remove leading '?'
        this.renderPage(searchParams);
      });
    });
  }

  /**
   * Core method: Fetch and render updated collection page via AJAX
   * @param {string} searchParams - URL search parameters (e.g., "filter.v.price.gte=50&sort_by=price-ascending")
   * @param {boolean} updateURL - Whether to update browser URL with history.pushState
   */
  async renderPage(searchParams, updateURL = true) {
    try {
      // 1. Build section URL using Shopify Section Rendering API
      const url = this.getSectionUrl(searchParams);

      // 2. Check cache first (Step 12)
      if (this.cache.has(url)) {
        const cachedHtml = this.cache.get(url);

        // Parse cached HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(cachedHtml, 'text/html');

        // Update DOM sections (no loading state needed for cached response)
        this.updateProductGrid(doc);
        this.updatePagination(doc);
        this.updateProductCount(doc);
        this.updateDesktopFilters(doc);
        this.updateMobileDrawer(doc);
        this.updateMobileDrawerTrigger(doc);
        this.updateSortDropdown(doc);

        // Update URL if needed
        if (updateURL) {
          this.updateURL(searchParams);
        }

        // Scroll to top of grid
        this.scrollToTop();

        return;
      }

      // 3. Show loading state (only for non-cached requests)
      this.showLoadingState();

      // 4. Cancel any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      // 5. Fetch HTML from server
      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // 6. Add to cache
      this.addToCache(url, html);

      // 7. Parse response HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // 8. Update DOM sections
      this.updateProductGrid(doc);
      this.updatePagination(doc);
      this.updateProductCount(doc);
      this.updateDesktopFilters(doc);
      this.updateMobileDrawer(doc);
      this.updateMobileDrawerTrigger(doc);
      this.updateSortDropdown(doc);

      // 9. Hide loading state
      this.hideLoadingState();

      // 10. Update URL if needed
      if (updateURL) {
        this.updateURL(searchParams);
      }

      // 11. Scroll to top of grid
      this.scrollToTop();

    } catch (error) {
      // Ignore aborted requests
      if (error.name === 'AbortError') {
        return;
      }

      this.hideLoadingState();
      this.handleError(error);
    }
  }

  /**
   * Build Shopify Section Rendering API URL
   * @param {string} searchParams - URL search parameters
   * @returns {string} Section URL
   */
  getSectionUrl(searchParams) {
    return `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    // Add loading overlay to product grid
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
    }

    // Update product grid opacity
    if (this.productGrid) {
      this.productGrid.classList.add('opacity-25');
    }
  }

  /**
   * Hide loading state
   */
  hideLoadingState() {
    // Hide loading overlay
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add('hidden');
    }

    // Remove product grid opacity
    if (this.productGrid) {
      this.productGrid.classList.remove('opacity-25');
    }
  }

  /**
   * Update product grid from parsed HTML
   * @param {Document} doc - Parsed HTML document
   */
  updateProductGrid(doc) {
    const newGrid = doc.getElementById('ProductGrid');
    if (newGrid && this.productGrid) {
      this.productGrid.innerHTML = newGrid.innerHTML;
    }
  }

  /**
   * Update pagination from parsed HTML
   * @param {Document} doc - Parsed HTML document
   */
  updatePagination(doc) {
    const newPagination = doc.getElementById('Pagination');
    if (newPagination && this.pagination) {
      this.pagination.innerHTML = newPagination.innerHTML;

      // Re-bind pagination events after DOM update
      this.bindPaginationEvents();
    }
  }

  /**
   * Update product count from parsed HTML
   * @param {Document} doc - Parsed HTML document
   */
  updateProductCount(doc) {
    const newCount = doc.getElementById('ProductCount');
    if (newCount && this.productCount) {
      this.productCount.innerHTML = newCount.innerHTML;
    }

    // Update mobile drawer trigger count
    const newTrigger = doc.querySelector('[data-drawer-trigger]');
    if (newTrigger && this.mobileDrawerTrigger) {
      const newTriggerCount = newTrigger.querySelector('[data-drawer-product-count]');
      const currentTriggerCount = this.mobileDrawerTrigger.querySelector('[data-drawer-product-count]');

      if (newTriggerCount && currentTriggerCount) {
        currentTriggerCount.textContent = newTriggerCount.textContent;
      }
    }
  }

  /**
   * Update desktop filters from parsed HTML
   * @param {Document} doc - Parsed HTML document
   */
  updateDesktopFilters(doc) {
    const newFilters = doc.getElementById('DesktopFilters');
    if (newFilters && this.desktopFilters) {
      this.desktopFilters.innerHTML = newFilters.innerHTML;

      // Update form reference after DOM update
      this.desktopForm = document.getElementById('filter-form');

      // Re-bind desktop filter events after DOM update
      this.bindDesktopFilterEvents();

      // Re-bind desktop reset filter button events after DOM update
      this.bindDesktopResetFilterButtons();

      // Re-bind active filter pills after DOM update
      this.bindActiveFilterPills();

      // Re-initialize price filter events (managed by CollectionFilters class)
      if (window.collectionFilters && window.collectionFilters.initPriceFilters) {
        window.collectionFilters.initPriceFilters();
      }
    }
  }

  /**
   * Update mobile drawer from parsed HTML
   * @param {Document} doc - Parsed HTML document
   */
  updateMobileDrawer(doc) {
    const newDrawer = doc.getElementById('MobileDrawerFilters');
    if (newDrawer && this.mobileDrawer) {
      this.mobileDrawer.innerHTML = newDrawer.innerHTML;

      // Update form reference after DOM update
      this.mobileForm = document.getElementById('filter-form-mobile');

      // Re-bind mobile drawer "View Results" button
      this.bindMobileDrawerEvents();

      // Re-bind mobile reset filter button events after DOM update
      this.bindMobileResetFilterButtons();

      // Re-initialize accordion toggle events (managed by CollectionFilters class)
      if (window.collectionFilters && window.collectionFilters.initAccordions) {
        window.collectionFilters.initAccordions();
      }

      // Re-initialize price filter events (managed by CollectionFilters class)
      if (window.collectionFilters && window.collectionFilters.initPriceFilters) {
        window.collectionFilters.initPriceFilters();
      }
    }
  }

  /**
   * Update mobile drawer trigger button (includes active filter count badge)
   * @param {Document} doc - Parsed HTML document
   */
  updateMobileDrawerTrigger(doc) {
    const newTrigger = doc.getElementById('MobileDrawerTrigger');
    if (newTrigger && this.mobileDrawerTriggerContainer) {
      this.mobileDrawerTriggerContainer.innerHTML = newTrigger.innerHTML;

      // Update CollectionFilters instance to point to new button element
      if (window.collectionFilters) {
        window.collectionFilters.drawerTrigger = document.querySelector('[data-drawer-trigger]');

        // Re-bind just the drawer trigger click event
        if (window.collectionFilters.drawerTrigger) {
          window.collectionFilters.drawerTrigger.addEventListener('click', () => {
            window.collectionFilters.openDrawer();
          });
        }
      }
    }
  }

  /**
   * Update sort dropdown from parsed HTML
   * @param {Document} doc - Parsed HTML document
   */
  updateSortDropdown(doc) {
    const newSortSelect = doc.querySelector('[data-sort-select]');
    const currentSortSelect = document.querySelector('[data-sort-select]');

    if (newSortSelect && currentSortSelect) {
      // Update the selected value (element stays in DOM, so event listener persists)
      currentSortSelect.value = newSortSelect.value;
    }
  }

  /**
   * Update browser URL with history.pushState
   * @param {string} searchParams - URL search parameters
   */
  updateURL(searchParams) {
    const newUrl = searchParams
      ? `${window.location.pathname}?${searchParams}`
      : window.location.pathname;

    history.pushState({ searchParams }, '', newUrl);
  }

  /**
   * Setup browser back/forward button handling
   */
  setupHistoryManagement() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state?.searchParams || '';
      this.renderPage(searchParams, false); // Don't push state again
    });
  }

  /**
   * Add HTML response to cache with size limit (Step 12)
   * @param {string} url - The section URL
   * @param {string} html - The HTML response to cache
   */
  addToCache(url, html) {
    // Remove oldest entry if cache is full (FIFO)
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    // Add new entry
    this.cache.set(url, html);
  }

  /**
   * Clear the response cache (called on page unload)
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Scroll to top of collection content area (includes product count and sort controls)
   */
  scrollToTop() {
    const collectionContent = document.getElementById('CollectionContent');
    const scrollTarget = collectionContent || this.productGrid;

    if (scrollTarget) {
      const offset = 100; // Offset for sticky header
      const targetTop = scrollTarget.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Handle errors with user-friendly message
   * @param {Error} error - Error object
   */
  handleError(error) {
    console.error('CollectionFiltersAjax error:', error);

    // Create error toast
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-4 right-4 bg-red-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center gap-4';
    toast.innerHTML = `
      <div>
        <p class="font-axiom-bold mb-1">Failed to update filters</p>
        <p class="text-sm">Please try again or refresh the page.</p>
      </div>
      <button class="bg-white text-red-600 px-4 py-2 rounded font-axiom-demi-bold hover:bg-gray-100 transition-colors" onclick="window.location.reload()">
        Reload
      </button>
    `;

    document.body.appendChild(toast);

    // Auto-remove toast after 8 seconds
    setTimeout(() => {
      toast.remove();
    }, 8000);
  }

  /**
   * Debounce utility function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

/**
 * Initialize collection filters AJAX when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-section-id]')) {
    window.collectionFiltersAjax = new CollectionFiltersAjax();
  }
});

/**
 * Reinitialize if page is loaded via Shopify's theme editor
 */
if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (document.querySelector('[data-section-id]')) {
      window.collectionFiltersAjax = new CollectionFiltersAjax();
    }
  });
}

/**
 * Clear cache on page unload (Step 12)
 */
window.addEventListener('beforeunload', () => {
  if (window.collectionFiltersAjax) {
    window.collectionFiltersAjax.clearCache();
  }
});
